import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation } from '@apollo/react-hooks'
import { toastr } from 'react-redux-toastr'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { User, Check, CheckCheck, RotateCcw, X, LoaderCircle } from 'lucide-react'
import { submittalVerdictVariant, submittalWorkflowStepVariant } from '@/lib/status-variants'

import * as Query from '../../../api/gql/query'
import * as Mutation from '../../../api/gql/mutation'
import {
  WorkflowProgressFlow,
  WorkflowProgressState,
  WorkflowProgressStep,
  findFolderName,
  STATE_ICON,
  STATE_LABEL_KEY,
} from '../components/workflow-flow'

const DASH = '—'

const formatDate = (t: (key: string, opts?: any) => string, value?: string | null) => {
  if (!value) return DASH
  return new Date(value).toLocaleDateString(t('date_locale') || 'en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

const VERDICTS = ['APPROVED', 'APPROVED_AS_NOTED', 'REVISE_AND_RESUBMIT', 'REJECTED'] as const
type Verdict = (typeof VERDICTS)[number]

const VERDICT_ICON: Record<Verdict, React.ReactNode> = {
  APPROVED: <Check className="h-3.5 w-3.5" />,
  APPROVED_AS_NOTED: <CheckCheck className="h-3.5 w-3.5" />,
  REVISE_AND_RESUBMIT: <RotateCcw className="h-3.5 w-3.5" />,
  REJECTED: <X className="h-3.5 w-3.5" />,
}

interface VerdictPrompt {
  reviewId: string
  verdict: Verdict
}

interface VerdictConfirmDialogProps {
  prompt: VerdictPrompt
  onCancel: () => void
  refetch: () => void
}

/**
 * Mini confirm dialog for the assigned reviewer's own verdict — mounted only
 * while a verdict button was just clicked (see the `{verdictPrompt && ...}`
 * guard below), same conditional-mount convention as the page's other
 * dialogs.
 */
const VerdictConfirmDialog: React.FC<VerdictConfirmDialogProps> = ({ prompt, onCancel, refetch }) => {
  const { t } = useTranslation()
  const [comment, setComment] = useState('')
  const [setReviewVerdict, { loading: submitting }] = useMutation(Mutation.SET_SUBMITTAL_REVIEW_VERDICT)

  const handleClose = () => {
    if (submitting) return
    onCancel()
  }

  const handleConfirm = async () => {
    try {
      await setReviewVerdict({
        variables: { reviewId: prompt.reviewId, verdict: prompt.verdict, comment: comment.trim() || null },
      })
      toastr.success('', t('Submittals_review_verdict_success'))
      refetch()
      onCancel()
    } catch (error: any) {
      toastr.error(t('Common_error'), error.message)
    }
  }

  return (
    <Dialog open onOpenChange={(next) => { if (!next) handleClose() }}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>{t('Submittals_review_verdict_confirm_title')}</DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <Badge variant={submittalVerdictVariant(prompt.verdict)} className="whitespace-nowrap font-medium">
            {t(`Submittal_verdict_${prompt.verdict}`)}
          </Badge>

          <div className="space-y-1.5">
            <Label htmlFor="review-verdict-comment">{t('Submittals_review_verdict_comment_label')}</Label>
            <Textarea
              id="review-verdict-comment"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder={t('Submittals_review_verdict_comment_placeholder')}
              rows={3}
            />
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 border-t pt-4">
          <Button type="button" variant="outline" onClick={handleClose} disabled={submitting}>
            {t('Common_cancel')}
          </Button>
          <Button type="button" onClick={handleConfirm} disabled={submitting}>
            {submitting && <LoaderCircle className="h-4 w-4 animate-spin" />}
            {t('Submittals_review_verdict_confirm_btn')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}

interface WorkflowStepPlan {
  index: number
  name: string
  durationDays: number | null
  reviewerIds: string[]
}

/** Verdicts that close a step early: the engine deletes the step's remaining
 *  undecided rows the moment one lands (see lifecycle.ts). */
const NEGATIVE_VERDICTS = ['REVISE_AND_RESUBMIT', 'REJECTED']

/**
 * State of one `workflowPlan.steps` entry, derived from ITS OWN review rows —
 * no rows yet is "planned", rows with at least one undecided is "active" (the
 * engine only ever leaves undecided rows on the one currently-open step), all
 * rows decided is "done", and "returned" is that same done state reached
 * through a negative verdict.
 *
 * A rowless step needs `stepIndex` and the revision's cursor to be read
 * correctly, because "no rows" covers two opposite facts. The engine has
 * already walked past the step when its index is below `activeStepIndex` (it
 * opened nothing there because every reviewer was deactivated — STEP_SKIPPED
 * in lifecycle.ts) or when the cursor is cleared on a revision that was
 * submitted (a short-circuit ended the flow and stranded the rest). Either way
 * the step will never run, which is "skipped"; only a step the engine has yet
 * to reach is still "planned".
 *
 * Single source of truth for both readouts of the same fact: the step groups
 * below and the read-only WorkflowProgressFlow above them.
 */
const deriveStepProgress = (
  stepReviews: any[],
  isFinalized: boolean,
  stepIndex: number,
  activeStepIndex: number | null,
  isSubmitted: boolean,
): { state: WorkflowProgressState; dueDate: string | null; isOverdue: boolean } => {
  const hasRows = stepReviews.length > 0
  const isDone = hasRows && stepReviews.every((r) => r.verdict != null)
  const isActive = hasRows && !isDone
  const dueDate = stepReviews[0]?.dueDate ?? null
  const isOverdue = isActive && !isFinalized && !!dueDate && new Date(dueDate) < new Date()
  const isReturned = isDone && stepReviews.some((r) => NEGATIVE_VERDICTS.includes(r.verdict))
  const enginePassedIt = activeStepIndex != null ? stepIndex < activeStepIndex : isSubmitted

  const state: WorkflowProgressState = !hasRows
    ? enginePassedIt
      ? 'skipped'
      : 'planned'
    : isDone
      ? isReturned
        ? 'returned'
        : 'done'
      : 'active'

  return { state, dueDate, isOverdue }
}

interface ReviewRowProps {
  review: any
  canDecide: boolean
  onPickVerdict: (verdict: Verdict) => void
}

const ReviewRow: React.FC<ReviewRowProps> = ({ review, canDecide, onPickVerdict }) => {
  const { t } = useTranslation()

  return (
    <div className="rounded border px-3 py-2.5">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <User className="h-4 w-4 shrink-0 text-muted-foreground" />
          <span className="text-sm font-medium">{review.reviewer?.name || DASH}</span>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {review.verdict ? (
            <Badge variant={submittalVerdictVariant(review.verdict)} className="whitespace-nowrap font-medium">
              {t(`Submittal_verdict_${review.verdict}`)}
            </Badge>
          ) : (
            <Badge variant="info" className="whitespace-nowrap font-medium">
              {t('Submittal_review_pending')}
            </Badge>
          )}
          {review.decidedAt && (
            <span className="whitespace-nowrap text-xs text-muted-foreground">{formatDate(t, review.decidedAt)}</span>
          )}
        </div>
      </div>

      {review.comment && (
        <p className="mb-0 mt-1.5 whitespace-pre-wrap pl-6 text-sm text-muted-foreground">{review.comment}</p>
      )}

      {canDecide && (
        <div className="mt-2 flex flex-wrap items-center gap-1.5 pl-6">
          {VERDICTS.map((v) => (
            <Button key={v} type="button" variant="outline" size="sm" className="h-7 text-xs" onClick={() => onPickVerdict(v)}>
              {VERDICT_ICON[v]}
              {t(`Submittal_verdict_${v}`)}
            </Button>
          ))}
        </div>
      )}
    </div>
  )
}

interface StepGroupProps {
  step: WorkflowStepPlan
  stepReviews: any[]
  progress: { state: WorkflowProgressState; dueDate: string | null; isOverdue: boolean }
  activeStepIndex: number | null
  isFinalized: boolean
  isReviewOpen: boolean
  meId?: string
  onPickVerdict: (reviewId: string, verdict: Verdict) => void
}

/**
 * One `workflowPlan.steps` entry rendered as a bordered group (same visual
 * language as the flow editor's step cards — see components/workflow-flow).
 * The `progress` it renders is computed once per step by the card below and
 * handed to both readouts, and the badge's label/variant/icon come from the
 * same maps the flow node uses — previously this component ran its own
 * if-chain and collapsed `returned` into a green "Done", disagreeing with the
 * red "Returned" node directly above it for the same step.
 */
const StepGroup: React.FC<StepGroupProps> = ({
  step,
  stepReviews,
  progress,
  activeStepIndex,
  isFinalized,
  isReviewOpen,
  meId,
  onPickVerdict,
}) => {
  const { t } = useTranslation()

  const { state, dueDate, isOverdue } = progress
  // Both mean "this step produced no verdicts"; only the label separates the
  // step still to come from the one that will never run.
  const hasNoRows = state === 'planned' || state === 'skipped'

  return (
    <div className="space-y-2 rounded-md border p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-sm font-semibold">
          {step.index + 1}. {step.name}
        </span>
        <div className="flex shrink-0 flex-wrap items-center gap-2">
          {/* Stays the plain state even when overdue — unlike the compact flow
              node, this row has space for the separate red "overdue · date"
              badge below, and two red badges would double-signal one fact. */}
          <Badge variant={submittalWorkflowStepVariant(state)} className="whitespace-nowrap font-medium">
            {STATE_ICON[state]}
            {t(STATE_LABEL_KEY[state])}
          </Badge>
          {state === 'active' &&
            dueDate &&
            (isOverdue ? (
              <Badge variant="danger" className="whitespace-nowrap font-medium">
                {t('Submittals_wf_overdue')} · {formatDate(t, dueDate)}
              </Badge>
            ) : (
              <span className="whitespace-nowrap text-xs text-muted-foreground">
                {t('Submittals_wf_due')}: {formatDate(t, dueDate)}
              </span>
            ))}
        </div>
      </div>

      {hasNoRows ? (
        <p className="text-xs text-muted-foreground">
          {t('Submittals_wf_step_planned_count', { count: step.reviewerIds.length })}
        </p>
      ) : (
        <div className="space-y-2">
          {stepReviews.map((review) => (
            <ReviewRow
              key={review.id}
              review={review}
              canDecide={
                !isFinalized &&
                isReviewOpen &&
                !!meId &&
                review.reviewerId === meId &&
                review.stepIndex === activeStepIndex
              }
              onPickVerdict={(verdict) => onPickVerdict(review.id, verdict)}
            />
          ))}
        </div>
      )}
    </div>
  )
}

interface SubmittalReviewsCardProps {
  submittal: any
  /** The currently DISPLAYED revision — same selection SubmittalAttachmentsCard
   *  uses (lifted to index.tsx), so both cards always describe the same revision. */
  revision: any
  meId?: string
  refetch: () => void
}

/**
 * Reviews of the displayed revision — flat rows for a plan-less revision
 * (byte-identical to phase 1), or grouped by `workflowPlan.steps` when the
 * revision was submitted through a template (see StepGroup above). Renders
 * nothing at all (not even an empty-state) when that revision has no
 * reviews yet. Zero rows is NOT the same fact as "never submitted" on the flat
 * path — createSubmittalRevision copies the prior revision's reviewer rows
 * forward as fresh Pending reviews the moment the new revision is created (see
 * lifecycle.ts), so a reopened or resubmitted flat revision still shows those
 * Pending rows here before it has been submitted itself.
 *
 * On the templated path zero rows can only mean un-submitted: submit writes
 * `workflowPlan` and step 0's rows in one transaction and refuses outright when
 * no step can open ("Every step of this workflow has only deactivated
 * reviewers"), so a submitted templated revision always carries at least one
 * row. The plan copied onto a restart DRAFT is therefore a PREFILL for the
 * resubmit dialog, not a live schedule — rendering it made every step read
 * "Planned", promising a review round that nothing had scheduled yet.
 */
const SubmittalReviewsCard: React.FC<SubmittalReviewsCardProps> = ({ submittal, revision, meId, refetch }) => {
  const { t } = useTranslation()
  const [verdictPrompt, setVerdictPrompt] = useState<VerdictPrompt | null>(null)

  const reviews: any[] = revision?.reviews || []
  const planSteps: WorkflowStepPlan[] | null = revision?.workflowPlan?.steps ?? null
  const targetFolderId: string | null = revision?.workflowPlan?.targetFolderId ?? null

  // The stored plan only carries the folder id, so the Publish node's label has
  // to be looked up. Skipped unless there is one; the same query FolderPicker
  // runs, so it is usually an Apollo cache hit.
  const { data: foldersData } = useQuery(Query.DOC_FOLDER_TREE, {
    variables: { projectId: submittal?.projectId },
    skip: !submittal?.projectId || !targetFolderId,
  })

  if (reviews.length === 0) return null

  const isFinalized = revision.finalVerdict != null
  const isReviewOpen = ['OPEN', 'UNDER_REVIEW'].includes(submittal.status)
  const activeStepIndex: number | null = revision.activeStepIndex ?? null
  const isSubmitted = revision.submittedAt != null

  const sortedPlanSteps = planSteps ? [...planSteps].sort((a, b) => a.index - b.index) : null
  // One derivation per step, shared by the flow above and the groups below, so
  // the two can never disagree about the same step.
  const stepProgress = (sortedPlanSteps || []).map((step) => ({
    step,
    stepReviews: reviews.filter((r) => r.stepIndex === step.index),
    progress: deriveStepProgress(
      reviews.filter((r) => r.stepIndex === step.index),
      isFinalized,
      step.index,
      activeStepIndex,
      isSubmitted,
    ),
  }))
  const progressSteps: WorkflowProgressStep[] = stepProgress.map(({ step, progress }) => ({
    index: step.index,
    name: step.name,
    state: progress.state,
    overdue: progress.isOverdue,
    dueLabel: progress.dueDate ? formatDate(t, progress.dueDate) : null,
  }))

  return (
    <Card className="shadow-sm border-0">
      <CardHeader>
        <CardTitle className="text-base">{t('Submittals_reviews_title')}</CardTitle>
      </CardHeader>
      <CardContent className={sortedPlanSteps ? 'space-y-3' : 'space-y-2'}>
        {sortedPlanSteps && (
          <WorkflowProgressFlow
            steps={progressSteps}
            targetFolderName={findFolderName(foldersData?.docFolderTree, targetFolderId)}
          />
        )}

        {sortedPlanSteps ? (
          stepProgress.map(({ step, stepReviews, progress }) => (
            <StepGroup
              key={step.index}
              step={step}
              stepReviews={stepReviews}
              progress={progress}
              activeStepIndex={activeStepIndex}
              isFinalized={isFinalized}
              isReviewOpen={isReviewOpen}
              meId={meId}
              onPickVerdict={(reviewId, verdict) => setVerdictPrompt({ reviewId, verdict })}
            />
          ))
        ) : (
          reviews.map((review) => (
            <ReviewRow
              key={review.id}
              review={review}
              canDecide={!isFinalized && isReviewOpen && !!meId && review.reviewerId === meId}
              onPickVerdict={(verdict) => setVerdictPrompt({ reviewId: review.id, verdict })}
            />
          ))
        )}
      </CardContent>

      {verdictPrompt && (
        <VerdictConfirmDialog
          prompt={verdictPrompt}
          onCancel={() => setVerdictPrompt(null)}
          refetch={refetch}
        />
      )}
    </Card>
  )
}

export default SubmittalReviewsCard
